import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EmailOutlined, LockOutlined, PasswordOutlined } from '@mui/icons-material';
import AuthSplitLayout from '../components/AuthSplitLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canRequest = useMemo(() => email.trim().length > 3, [email]);
  const canVerify = useMemo(
    () => email.trim().length > 3 && otp.trim().length >= 4 && newPassword.length >= 8,
    [email, otp, newPassword]
  );

  const requestOtp = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.post(`${API_URL}/auth/password-reset/request`, { email });
      if (response.data?.success) {
        setSuccess(response.data?.message || 'Code sent');
        setStep('verify');
      } else {
        setError(response.data?.message || 'Failed to request reset code');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to request reset code');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.post(`${API_URL}/auth/password-reset/verify`, {
        email,
        otp,
        newPassword,
      });
      if (response.data?.success) {
        setSuccess(response.data?.message || 'Password reset successfully');
        setTimeout(() => navigate('/login'), 900);
      } else {
        setError(response.data?.message || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      title={
        <>
          Secure your
          <br />
          progress.
        </>
      }
      subtitle="Reset your password quickly and get back to the fasting routines, nutrition logs, and insights that keep you moving."
      rightTitle="Reset password"
      rightSubtitle={
        step === 'request'
          ? "Enter your email and we'll send a code."
          : 'Enter the code from your email and set a new password.'
      }
      footer={
        <Typography variant="body2" sx={{ color: 'rgba(31,43,34,0.62)', textAlign: 'center' }}>
          Remembered your password?{' '}
          <Link to="/login" style={{ color: '#b46f47', fontWeight: 600, textDecoration: 'none' }}>
            Log in
          </Link>
        </Typography>
      }
    >
      {(error || success) && (
        <Alert
          severity={error ? 'error' : 'success'}
          sx={{
            mb: 3,
            bgcolor: error ? 'rgba(230,124,115,0.08)' : 'rgba(123,196,142,0.1)',
            color: error ? '#7b2f28' : '#1d4f2e',
          }}
        >
          {error || success}
        </Alert>
      )}

      {step === 'request' ? (
        <Box component="form" onSubmit={requestOtp}>
          <TextField
            fullWidth
            label="Email address"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined sx={{ color: 'rgba(31,43,34,0.45)' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, bgcolor: '#122019', color: '#f5efe4', '&:hover': { bgcolor: '#1a2d24' } }}
            disabled={loading || !canRequest}
          >
            {loading ? 'Sending code…' : 'Send reset code'}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={verifyOtp}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email address"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: 'rgba(31,43,34,0.45)' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Reset code"
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PasswordOutlined sx={{ color: 'rgba(31,43,34,0.45)' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New password"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              helperText="Use at least 8 characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: 'rgba(31,43,34,0.45)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, bgcolor: '#122019', color: '#f5efe4', '&:hover': { bgcolor: '#1a2d24' } }}
            disabled={loading || !canVerify}
          >
            {loading ? 'Resetting…' : 'Reset password'}
          </Button>
        </Box>
      )}
    </AuthSplitLayout>
  );
}
